import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Gauge, Plus, Loader2, SlidersHorizontal, Download } from 'lucide-react';
import { api } from '@/lib/api';
import { DonutCard, SEM } from '@/components/client/SuiteAnalytics';
import { toast } from 'sonner';
import { PerfCycleDetail } from '@/components/client/PerfCycleDetail';

const STATUS_STYLE = {
  draft: 'bg-muted text-muted-foreground',
  active: 'bg-info/10 text-info border-info/20',
  closed: 'bg-success/10 text-success border-success/20',
};
const TH_KEYS = ['gap', 'strength', 'strength_front_min', 'development', 'alert', 'alert_front'];

export default function ClientTalentPerformance() {
  const { t, i18n } = useTranslation();
  const es = i18n.language?.startsWith('es');
  const [cycles, setCycles] = useState(null);
  const [selected, setSelected] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [thOpen, setThOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [instruments, setInstruments] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', period_start: '', period_end: '', due_date: '' });

  const load = useCallback(() => {
    api.get('/client/talentperformance/cycles').then((r) => setCycles(r.data)).catch((e) => toast.error(tpError(t, e)));
    api.get('/client/talentperformance/settings').then((r) => setSettings(r.data)).catch(() => {});
    api.get('/client/talentperformance/instruments').then((r) => setInstruments(r.data)).catch(() => {});
  }, [t]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setBusy(true);
    try {
      const r = await api.post('/client/talentperformance/cycles', {
        name: form.name, description: form.description || null,
        period_start: form.period_start || null, period_end: form.period_end || null,
        due_date: form.due_date || null,
      });
      toast.success(t('talentperformance.cycle.createdOk', { defaultValue: 'Cycle created as draft.' }));
      setCreateOpen(false);
      setForm({ name: '', description: '', period_start: '', period_end: '', due_date: '' });
      load();
      setSelected(r.data.id);
    } catch (e) { toast.error(tpError(t, e)); } finally { setBusy(false); }
  };

  const saveThresholds = async () => {
    setBusy(true);
    try {
      await api.put('/client/talentperformance/settings', settings.thresholds);
      toast.success(t('talentperformance.settings.savedOk', { defaultValue: 'Thresholds saved — future cycles will pin these values.' }));
      setThOpen(false);
    } catch (e) { toast.error(tpError(t, e)); } finally { setBusy(false); }
  };

  const exportInstrument = async (qid, fmt) => {
    try {
      const r = await api.get(`/client/talentperformance/instruments/${qid}/export/${fmt}`, { params: { lang: es ? 'es' : 'en' }, responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(r.data); a.download = `instrument.${fmt}`; a.click();
    } catch (e) { toast.error(tpError(t, e)); }
  };

  return (
    <div className="min-h-screen" data-testid="client-talentperformance">
      <Topbar title="TalentPerformance" subtitle={t('talentperformance.subtitle', { defaultValue: 'Governed three-front performance evaluation and development planning' })} />
      <div className="p-6 space-y-6">
        {selected ? (
          <PerfCycleDetail cycleId={selected} onBack={() => { setSelected(null); load(); }} />
        ) : (
          <>
            <div className="flex items-center gap-2 flex-wrap">
              <Button className="gap-1.5" onClick={() => setCreateOpen(true)} data-testid="tp-new-cycle">
                <Plus className="h-4 w-4" />{t('talentperformance.cycle.new', { defaultValue: 'New evaluation cycle' })}
              </Button>
              <Button variant="outline" className="gap-1.5" onClick={() => setThOpen(true)} data-testid="tp-open-thresholds">
                <SlidersHorizontal className="h-4 w-4" />{t('talentperformance.settings.thresholds', { defaultValue: 'Analytical thresholds' })}
              </Button>
            </div>

            {/* Founder UAT 2026-06 — executive visual analytics (real data) */}
            {Array.isArray(cycles) && cycles.length > 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="tp-analytics-row">
                <DonutCard
                  title={t('talentperformance.analytics.statusTitle', { defaultValue: 'Ciclos por estado' })}
                  testid="tp-chart-status"
                  accent={SEM.blue}
                  data={[
                    { name: t('talentperformance.status.draft', { defaultValue: 'Borrador' }), value: cycles.filter((c) => c.status === 'draft').length, color: SEM.yellow },
                    { name: t('talentperformance.status.active', { defaultValue: 'Activo' }), value: cycles.filter((c) => c.status === 'active').length, color: SEM.orange },
                    { name: t('talentperformance.status.closed', { defaultValue: 'Cerrado' }), value: cycles.filter((c) => c.status === 'closed').length, color: SEM.green },
                  ]}
                  emptyText={t('talentperformance.cycle.empty', { defaultValue: 'No evaluation cycles yet — create the first one to begin.' })}
                />
              </div>
            )}

            <Card className="shadow-card overflow-hidden">
              <div className="p-4 border-b flex items-center gap-2">
                <Gauge className="h-4 w-4 text-info" />
                <h4 className="text-sm font-semibold">{t('talentperformance.cycle.listTitle', { defaultValue: 'Evaluation cycles' })}</h4>
              </div>
              {cycles === null ? (
                <div className="p-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" /></div>
              ) : cycles.length === 0 ? (
                <p className="p-8 text-sm text-muted-foreground text-center" data-testid="tp-cycles-empty">{t('talentperformance.cycle.empty', { defaultValue: 'No evaluation cycles yet — create the first one to begin.' })}</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[640px]">
                    <thead>
                      <tr className="border-b bg-muted/40 text-left text-[12px] uppercase tracking-wide text-muted-foreground">
                        <th className="px-4 py-2.5 font-medium">{t('talentperformance.fields.cycle', { defaultValue: 'Cycle' })}</th>
                        <th className="px-4 py-2.5 font-medium">{t('talentperformance.fields.period', { defaultValue: 'Period' })}</th>
                        <th className="px-4 py-2.5 font-medium">{t('talentperformance.fields.progress', { defaultValue: 'Progress' })}</th>
                        <th className="px-4 py-2.5 font-medium">{t('talentperformance.fields.status', { defaultValue: 'Status' })}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cycles.map((c) => (
                        <tr key={c.id} className="border-b last:border-0 hover:bg-muted/30 cursor-pointer" onClick={() => setSelected(c.id)} data-testid={`tp-cycle-row-${c.id}`}>
                          <td className="px-4 py-2.5 font-medium">{c.name}</td>
                          <td className="px-4 py-2.5 text-muted-foreground">{c.period_start || '—'} → {c.period_end || '—'}</td>
                          <td className="px-4 py-2.5">{c.evaluations_done}/{c.evaluations_total}</td>
                          <td className="px-4 py-2.5"><Badge variant="outline" className={STATUS_STYLE[c.status]}>{t(`talentperformance.status.${c.status}`)}</Badge></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            <Card className="p-4 shadow-card space-y-2">
              <h4 className="text-sm font-semibold">{t('talentperformance.instruments.title', { defaultValue: 'Available TalentPerformance instruments (Evaluation Library)' })}</h4>
              <p className="text-xs text-muted-foreground">{t('talentperformance.instruments.hint', { defaultValue: 'Instruments are governed in the Evaluation Library. Your CTH advisor can create tailored versions for your organization via Duplicate & Adapt.' })}</p>
              <div className="space-y-1.5" data-testid="tp-instruments-list">
                {instruments.map((q) => (
                  <div key={q.id} className="flex items-center justify-between gap-2 rounded border border-border/60 px-3 py-2">
                    <p className="text-sm truncate">{es ? q.name : (q.name_en || q.name)}</p>
                    <div className="flex gap-1 shrink-0">
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs gap-1" onClick={() => exportInstrument(q.id, 'pdf')} data-testid={`tp-instr-pdf-${q.id}`}><Download className="h-3 w-3" />PDF</Button>
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs gap-1" onClick={() => exportInstrument(q.id, 'docx')}><Download className="h-3 w-3" />Word</Button>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </>
        )}
      </div>

      {/* Create cycle */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="tp-create-dialog">
          <DialogHeader>
            <DialogTitle>{t('talentperformance.cycle.new', { defaultValue: 'New evaluation cycle' })}</DialogTitle>
            <DialogDescription>{t('talentperformance.cycle.newDesc', { defaultValue: 'The cycle starts as a draft: configure fronts, instruments, weighting and employees before activating.' })}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>{t('talentperformance.fields.name', { defaultValue: 'Name' })}</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder={t('talentperformance.cycle.namePh', { defaultValue: 'e.g. 2026 Annual Performance Evaluation' })} data-testid="tp-cycle-name" />
            </div>
            <div className="space-y-1">
              <Label>{t('talentperformance.fields.description', { defaultValue: 'Description (optional)' })}</Label>
              <Textarea rows={2} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} data-testid="tp-cycle-desc" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {['period_start', 'period_end', 'due_date'].map((k) => (
                <div key={k} className="space-y-1">
                  <Label className="text-xs">{t(`talentperformance.fields.${k}`, { defaultValue: k })}</Label>
                  <Input type="date" value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} data-testid={`tp-cycle-${k}`} />
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
            <Button disabled={busy || form.name.trim().length < 3} onClick={create} data-testid="tp-create-submit">
              {busy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}{t('common.create', { defaultValue: 'Create' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Thresholds */}
      <Dialog open={thOpen} onOpenChange={setThOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="tp-thresholds-dialog">
          <DialogHeader>
            <DialogTitle>{t('talentperformance.settings.thresholds', { defaultValue: 'Analytical thresholds' })}</DialogTitle>
            <DialogDescription>{t('talentperformance.settings.thresholdsDesc', { defaultValue: 'Organization-level thresholds (0–100 scale). Each cycle pins the thresholds in force at activation — historical results never change retroactively.' })}</DialogDescription>
          </DialogHeader>
          {settings && (
            <div className="grid grid-cols-2 gap-3">
              {TH_KEYS.map((k) => (
                <div key={k} className="space-y-1">
                  <Label className="text-xs">{t(`talentperformance.settings.th_${k}`, { defaultValue: k })}</Label>
                  <Input type="number" value={settings.thresholds[k]}
                    onChange={(e) => setSettings((s) => ({ ...s, thresholds: { ...s.thresholds, [k]: Number(e.target.value) || 0 } }))}
                    data-testid={`tp-th-${k}`} />
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setThOpen(false)}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
            <Button disabled={busy || !settings} onClick={saveThresholds} data-testid="tp-th-save">
              {busy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}{t('common.save', { defaultValue: 'Save' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
